import { Add as AddIcon, Clear as ClearIcon } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useAccount } from 'wagmi';

import { WHITELIST_CREATOR_ADDRESS } from 'src/global/config/common.config';
import { RewardType } from 'src/global/enums/project.enum';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { useGetRecipientsQuery } from 'src/store/services/project.service';
import { isAllowedPercentage } from 'src/utils/numberFormat';

import { AdminNumberFormat } from '../AdminNumberFormat';
import AdminSelect from '../AdminSelect';

interface RecipientPercentProps {
  currentPhaseIndex: number;
}

export function RecipientPercent(props: RecipientPercentProps) {
  const { currentPhaseIndex } = props;
  const { hasDeployed } = useDeployment();
  const {
    control,
    watch,
    trigger,
    setValue,
    formState: { errors, isSubmitted, touchedFields },
  } = useFormContext<ProjectInputs>();
  const { address } = useAccount();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `phases.${currentPhaseIndex}.recipients`,
  });
  const isAdmin = address?.toLowerCase() === WHITELIST_CREATOR_ADDRESS?.toLowerCase();

  const { data: defaultRecipients = [], isLoading: isLoadingRecipients } = useGetRecipientsQuery(
    { address: address as string },
    { skip: !address },
  );

  const watchRecipients = watch(`recipients`) || [];
  const watchCurrentPhase = watch(`phases.${currentPhaseIndex}`);
  const referralPercent = watchCurrentPhase?.referalPercent || 0;
  const rewardType = watchCurrentPhase?.rewardType;

  const recipientMap = useMemo(
    () =>
      watchRecipients?.reduce((acc: Record<string, string>, recipient) => {
        acc[recipient.address] = recipient.name;
        return acc;
      }, {}),
    [watchRecipients],
  );

  const availablePercentage = useMemo(() => {
    if (rewardType === RewardType.ERC_20) {
      return 100 - referralPercent;
    }
    return 100;
  }, [rewardType, referralPercent]);

  const hasTouchedPhaseRecipient = watchCurrentPhase?.recipients?.some(
    (_, index) => touchedFields.phases?.[currentPhaseIndex]?.recipients?.[index]?.percent,
  );
  const shouldShowErrors =
    isSubmitted || touchedFields.phases?.[currentPhaseIndex]?.recipients || hasTouchedPhaseRecipient;

  useEffect(() => {
    if (
      isLoadingRecipients ||
      hasDeployed ||
      !address ||
      defaultRecipients.length === 0 ||
      !watchCurrentPhase?.recipients
    )
      return;

    const currentPhaseRecipients = watchCurrentPhase.recipients || [];
    // Check if default recipients are already filled
    const hasDefaultRecipients = defaultRecipients.some(defaultRecipient =>
      currentPhaseRecipients.some(
        phaseRecipient => phaseRecipient.address?.toLowerCase() === defaultRecipient.address?.toLowerCase(),
      ),
    );

    if (!hasDefaultRecipients && (currentPhaseRecipients.length <= 1 || currentPhaseRecipients.length === 0)) {
      const defaultRecipientsList = defaultRecipients.map(recipient => ({
        address: recipient.address,
        percent: recipient.percent || 0,
      }));

      const existingRecipients = currentPhaseRecipients.length > 0 ? [currentPhaseRecipients[0]] : [];
      const allRecipients = [...existingRecipients, ...defaultRecipientsList];

      setValue(`phases.${currentPhaseIndex}.recipients`, allRecipients);
    }
  }, [
    defaultRecipients,
    isLoadingRecipients,
    address,
    hasDeployed,
    watchCurrentPhase?.recipients,
    setValue,
    currentPhaseIndex,
  ]);

  useEffect(() => {
    if (!watchCurrentPhase?.recipients || hasDeployed || defaultRecipients.length === 0) return;

    const currentPhaseRecipients = watchCurrentPhase.recipients || [];
    let needsUpdate = false;
    const updatedRecipients = currentPhaseRecipients.map(recipient => {
      const defaultRecipient = defaultRecipients.find(
        dr => dr.address?.toLowerCase() === recipient.address?.toLowerCase(),
      );

      if (defaultRecipient) {
        if (recipient.percent !== defaultRecipient.percent) {
          needsUpdate = true;
          return {
            ...recipient,
            percent: defaultRecipient.percent || 0,
          };
        }
      }
      return recipient;
    });

    if (needsUpdate) {
      setValue(`phases.${currentPhaseIndex}.recipients`, updatedRecipients);
    }
  }, [watchCurrentPhase?.recipients, defaultRecipients, setValue, currentPhaseIndex, hasDeployed]);

  useEffect(() => {
    if (!watchCurrentPhase?.recipients || hasDeployed) return;

    const recipientsWithMissingNames = watchCurrentPhase.recipients.filter(
      (recipient, index) => recipient.address && !recipientMap[recipient.address] && index > 0, // Skip first recipient
    );

    if (recipientsWithMissingNames.length > 0) {
      // Clear recipient addresses that don't have names (except first one)
      const updatedRecipients = watchCurrentPhase.recipients.map((recipient, index) => ({
        ...recipient,
        address: index === 0 || (recipient.address && recipientMap[recipient.address]) ? recipient.address : '',
      }));

      // Update the phase with cleared addresses
      setValue(`phases.${currentPhaseIndex}.recipients`, updatedRecipients);
    }
  }, [watchCurrentPhase?.recipients, recipientMap, setValue, currentPhaseIndex, hasDeployed]);

  return (
    <>
      <Stack sx={{ paddingTop: 2 }} direction={'row'} gap={2}>
        <Box width={'30%'}>
          <Typography color={colors.neutral200} fontSize={'14px'} variant='h6'>
            Recipients
          </Typography>
        </Box>
        <Box width={'70%'}>
          <Typography color={colors.neutral200} fontSize={'14px'} variant='h6'>
            Funding Percentage
          </Typography>
        </Box>
      </Stack>
      <Box>
        {fields.map((recipient, recipientIndex) => {
          const currentRecipientAddress = watchCurrentPhase?.recipients?.[recipientIndex]?.address;
          const isDefaultRecipient = defaultRecipients.some(
            defaultRecipient => defaultRecipient.address?.toLowerCase() === currentRecipientAddress?.toLowerCase(),
          );

          const isError =
            !!errors?.phases?.[currentPhaseIndex]?.recipients?.message ||
            !!errors?.phases?.[currentPhaseIndex]?.recipients?.root ||
            !!errors?.phases?.[currentPhaseIndex]?.recipients?.[recipientIndex]?.percent;
          const errorMsg =
            errors?.phases?.[currentPhaseIndex]?.recipients?.message ||
            errors?.phases?.[currentPhaseIndex]?.recipients?.root?.message ||
            errors?.phases?.[currentPhaseIndex]?.recipients?.[recipientIndex]?.percent?.message;
          return (
            <Stack direction={'row'} gap={2} paddingY={1} key={recipient.id}>
              <Box width={'30%'}>
                <Controller
                  key={`phases.${currentPhaseIndex}.recipients.${recipientIndex}.address`}
                  name={`phases.${currentPhaseIndex}.recipients.${recipientIndex}.address`}
                  control={control}
                  render={({ field }) => (
                    <AdminSelect
                      disabled={isDefaultRecipient || recipientIndex === 0 || hasDeployed}
                      isError={!!errors?.phases?.[currentPhaseIndex]?.recipients?.[recipientIndex]?.address}
                      errorMsg={errors?.phases?.[currentPhaseIndex]?.recipients?.[recipientIndex]?.address?.message}
                      options={watchRecipients
                        .filter(recipient => recipient.address && recipient.name)
                        .map(recipient => ({
                          value: recipient.address,
                          label: recipient.name,
                          disabled: watchCurrentPhase.recipients.some(
                            phaseRecipient => phaseRecipient.address === recipient.address,
                          ),
                        }))}
                      {...field}
                      onChange={e => {
                        trigger(`phases.${currentPhaseIndex}.recipients`);
                        field.onChange(e);
                      }}
                    />
                  )}
                />
              </Box>
              <Stack direction='row' justifyContent={'space-between'} alignItems={'top'} width={'70%'} gap={1}>
                <Box width={'100%'}>
                  <Controller
                    name={`phases.${currentPhaseIndex}.recipients.${recipientIndex}.percent`}
                    control={control}
                    render={({ field: { onChange, ...rest } }) => {
                      const currentPhaseRecipients = watchCurrentPhase?.recipients || [];
                      const otherRecipientsTotal = currentPhaseRecipients
                        .filter((_, idx) => idx !== recipientIndex)
                        .reduce((sum, r) => sum + (r.percent || 0), 0);
                      const maxAllowed = availablePercentage - otherRecipientsTotal;

                      return (
                        <AdminNumberFormat
                          disabled={isDefaultRecipient || (recipientIndex === 0 && !isAdmin) || hasDeployed}
                          isAllowed={isAllowedPercentage}
                          onValueChange={values => {
                            // Prevent changing value if it's a default recipient
                            if (isDefaultRecipient) {
                              return;
                            }
                            const newValue = values.floatValue ? Number(values.floatValue) : 0;
                            onChange(newValue);
                            // Only trigger validation after user interaction
                            if (shouldShowErrors) {
                              trigger(`phases`);
                            }
                          }}
                          isError={shouldShowErrors && (isError || (maxAllowed < 0 && rest.value > 0))}
                          errorMsg={
                            shouldShowErrors
                              ? errorMsg ||
                                (maxAllowed < 0
                                  ? `Exceeds available percentage. Maximum: ${availablePercentage.toFixed(2)}%`
                                  : undefined)
                              : undefined
                          }
                          placeholder={'0%'}
                          suffix='%'
                          {...rest}
                        />
                      );
                    }}
                  />
                </Box>
                <Stack direction='row' spacing={1} alignItems={'center'} marginLeft={'10px'} marginTop={'0px'}>
                  {recipientIndex === watchCurrentPhase.recipients.length - 1 && (
                    <AddIcon
                      className={`${hasDeployed ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
                      sx={{
                        color: 'white',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        append({ address: '', percent: 0 });
                      }}
                    />
                  )}
                  {fields.length > 1 && recipientIndex > 0 && !isDefaultRecipient && (
                    <ClearIcon
                      className={`${hasDeployed ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
                      sx={{
                        color: colors.error,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        remove(recipientIndex);
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>
          );
        })}
      </Box>
    </>
  );
}
